// src/pages/dashboard/Dashboard.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useLocalization } from '../../contexts/LocalizationContext';
import { usePermissions } from '../../hooks/usePermissions';
import { 
  List, 
  Inbox, 
  Package,
  Box,
  Send,
  ChevronDown,
  Store
} from 'lucide-react';
import { getShops } from '../../services/api/shops';
import KeyMetrics from './components/KeyMetrics';
import OrderStatusDistribution from './components/OrderStatusDistribution';
import SalesTrendChart from './components/SalesTrendChart';
import TopSellingProducts from './components/TopSellingProducts';
import WorkQueueSummary from './components/WorkQueueSummary';
import InventoryAlerts from './components/InventoryAlerts';
import PackingActivity from './components/PackingActivity';
import CourierReturns from './components/CourierReturns';
import MyCommissionCard from './components/MyCommissionCard';
import { SubscriptionStatusBanner } from '../../components/common/Subscription';
import styles from './Dashboard.module.css';

const Dashboard = () => {
  const { user } = useAuth();
  const { t } = useLocalization();
  const { hasPermission } = usePermissions();

  const [selectedShop, setSelectedShop] = useState('all');
  const [shops, setShops] = useState([]);
  const [loadingShops, setLoadingShops] = useState(false);
  const [showShopDropdown, setShowShopDropdown] = useState(false);

  useEffect(() => {
    if (hasPermission('view_sales_reports')) {
      fetchShops();
    }
  }, []);

  const fetchShops = async () => {
    try {
      setLoadingShops(true);
      const response = await getShops({assigned: true});
      setShops(Array.isArray(response) ? response : (response.shops || []));
    } catch (error) {
      console.error('Error fetching shops:', error);
    } finally {
      setLoadingShops(false);
    }
  };

  const quickActions = [
    {
      label: t('dashboard.orderList'),
      icon: List,
      path: '/orders',
      permission: 'view_all_ecom_orders'
    },
    {
      label: t('dashboard.myWorkQueue'),
      icon: Inbox,
      path: '/order-assignments/my-queue',
      permission: 'view_work_queue'
    },
    {
      label: t('dashboard.productList'),
      icon: Package,
      path: '/products',
      permission: 'view_all_products'
    },
    {
      label: t('dashboard.newBatch'),
      icon: Box,
      path: '/packing-batches/create',
      permission: 'create_packing_batch'
    },
    {
      label: t('dashboard.dispatchOrders'),
      icon: Send,
      path: '/packing-batches/dispatch/single',
      permission: 'dispatch_single_orders'
    }
  ];

  const availableActions = quickActions.filter(action => 
    !action.permission || hasPermission(action.permission)
  );

  const getSelectedShopName = () => {
    if (selectedShop === 'all') return t('dashboard.allShops');
    const shop = shops.find(s => s.id === parseInt(selectedShop));
    return shop ? shop.name : t('dashboard.selectShop');
  };

  const canViewSalesReports = hasPermission('view_sales_reports');
  const canViewWorkQueue    = hasPermission('view_work_queue');

  return (
    <>
    <SubscriptionStatusBanner />
    <div className={styles.dashboardPage}>
      {/* Welcome Section */}
      <div className={styles.dashboardWelcome}>
        <div className={styles.welcomeContent}>
          <h1>{t('dashboard.welcomeBack', { name: user?.name })}</h1>
          <p>{t('dashboard.businessOverview')}</p>
        </div>
      </div>

      {/* Filters Section */}
      {canViewSalesReports && (
        <div className={styles.filtersSection}>
          <div className={styles.filterGroup}>
            <label className={styles.filterLabel}>{t('dashboard.selectShop')}</label>
            <div className={styles.dropdownWrapper}>
              <button 
                className={styles.filterButton}
                onClick={() => {
                  setShowShopDropdown(!showShopDropdown);
                }}
              >
                <Store size={16} />
                <span>{getSelectedShopName()}</span>
                <ChevronDown size={16} />
              </button>
              {showShopDropdown && (
                <div className={styles.dropdownMenu}>
                  <div 
                    className={`${styles.dropdownItem} ${selectedShop === 'all' ? styles.active : ''}`}
                    onClick={() => {
                      setSelectedShop('all');
                      setShowShopDropdown(false);
                    }}
                  >
                    {t('dashboard.allShops')}
                  </div>
                  {shops.map(shop => (
                    <div
                      key={shop.id}
                      className={`${styles.dropdownItem} ${selectedShop === shop.id ? styles.active : ''}`}
                      onClick={() => {
                        setSelectedShop(shop.id);
                        setShowShopDropdown(false);
                      }}
                    >
                      {shop.name}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Key Metrics */}
      {canViewSalesReports && (
        <KeyMetrics 
          shopId={selectedShop} 
          currency={user?.business?.currency || null} 
        />
      )}

      {/* Main Dashboard Grid */}
      <div className={styles.dashboardGrid}>
        {/* Sales Trend Chart - Full width */}
        {canViewSalesReports && (
          <div className={styles.fullWidthSection}>
            <SalesTrendChart 
              shopId={selectedShop}
              currency={user?.business?.currency || null} 
            />
          </div>
        )}

        {/* Work Queue Summary - always full width */}
        {canViewWorkQueue && (
          <div className={styles.fullWidthSection}>
            <WorkQueueSummary shopId={selectedShop} />
          </div>
        )}

        {/* Order Status Distribution */}
        {canViewSalesReports && (
          <div className={styles.gridItem}>
            <OrderStatusDistribution 
              shopId={selectedShop}
              currency={user?.business?.currency || null} 
            />
          </div>
        )}

        {/* Employee Commission Card */}
        <MyCommissionCard gridItemClassName={styles.gridItem} />

        {/* Packing Activity */}
        {hasPermission('view_all_packing_batches') && (
        <div className={styles.gridItem}>
          <PackingActivity 
            currency={user?.business?.currency || null}
          />
        </div>
        )}

        {/* Courier Returns */}
        {hasPermission('view_return_batches') && (
        <div className={styles.gridItem}>
          <CourierReturns 
            currency={user?.business?.currency || null}
          />
        </div>
        )}

        {/* Inventory Alerts */}
        {hasPermission('create_purchase_order') && (
        <div className={styles.gridItem}>
          <InventoryAlerts />
        </div>
        )}

        {/* Top Selling Products */}
        {canViewSalesReports && (
          <div className={styles.gridItem}>
            <TopSellingProducts 
              shopId={selectedShop}
              currency={user?.business?.currency || null}
            />
          </div>
        )}

        {/* Quick Actions */}
        {availableActions.length > 0 && (
          <div className={styles.gridItem}>
            <div className={styles.dashboardCard}>
              <div className={styles.cardHeader}>
                <h3>{t('dashboard.quickActions')}</h3>
              </div>
              <div className={styles.quickActions}>
                {availableActions.map((action, index) => {
                  const Icon = action.icon;
                  return (
                    <Link 
                      key={index}
                      to={action.path}
                      className={styles.actionButton}
                    >
                      <Icon size={18} />
                      <span>{action.label}</span>
                    </Link>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Account Information */}
        <div className={styles.gridItem}>
          <div className={styles.dashboardCard}>
            <div className={styles.cardHeader}>
              <h3>{t('dashboard.accountInfo')}</h3>
            </div>
            <div className={styles.accountInfo}>
              <div className={styles.infoItem}>
                <div className={styles.infoLabel}>{t('dashboard.businessName')}</div>
                <div className={styles.infoValue}>{user?.business?.name}</div>
              </div>
              <div className={styles.infoItem}>
                <div className={styles.infoLabel}>{t('dashboard.yourRole')}</div>
                <div className={styles.infoValue}>{user?.role?.name}</div>
              </div>
              <div className={styles.infoItem}>
                <div className={styles.infoLabel}>Email</div>
                <div className={styles.infoValue}>{user?.email}</div>
              </div>
              <div className={styles.infoItem}>
                <div className={styles.infoLabel}>{t('dashboard.permissions')}</div>
                <div className={styles.infoValue}>
                  <span className={styles.permissionsCount}>
                    {t('dashboard.permissionsCount', { 
                      count: user?.role?.permissions?.length || 0 
                    })}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default Dashboard;